<template>
  <div class="assistant-row" :class="{ 'is-error': message.error, 'is-aborted': message.aborted }">
    <div class="content">
      <div v-if="message.thinking && !hasContent" class="thinking">
        <span class="dot"/><span class="dot"/><span class="dot"/>
        <span class="thinking-text">ИИ думает над запросом...</span>
      </div>

      <div v-if="hasText" class="summary">
        <div
            v-if="!message.error && !message.aborted"
            class="md-body"
            v-html="renderedMarkdown"
        />
        <div v-else class="plain">{{ displayText }}</div>
      </div>

      <ChartFromAi v-if="message.chart" :chart="message.chart"/>

      <ToolStepsBreakdown
          v-if="message.steps && message.steps.length"
          :steps="message.steps"
      />

      <div v-if="hasMethodology" class="details">
        <button class="link-btn" @click="showDetails = !showDetails">
          {{ showDetails ? 'Скрыть подробности' : 'Подробнее' }}
        </button>
        <div v-if="showDetails" class="details-body">
          <div v-if="message.methodology" class="block">
            <div class="label">Как ИИ это посчитала</div>
            <div class="value">{{ message.methodology }}</div>
          </div>
          <div v-if="message.sql_used" class="block">
            <div class="label">Финальный SQL</div>
            <pre class="sql hljs"><code v-html="highlightedSql"/></pre>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ChartFromAi from './ChartFromAi.vue';
import ToolStepsBreakdown from './ToolStepsBreakdown.vue';
import { renderMarkdown } from '@/common/markdown';
import { highlightSql } from '@/common/sqlHighlight';

export default {
  name: 'AssistantMessage',
  components: { ChartFromAi, ToolStepsBreakdown },
  props: {
    message: { type: Object, required: true },
  },
  data() {
    return { showDetails: false };
  },
  computed: {
    renderedMarkdown() {
      return renderMarkdown(this.displayText);
    },
    highlightedSql() {
      return highlightSql(this.message.sql_used || '');
    },
    hasText() {
      return Boolean(this.displayText && this.displayText.length);
    },
    displayText() {
      if (this.message.error) {
        return `ИИ не справилась: ${this.message.errorMessage || 'неизвестная ошибка'}. Попробуйте переформулировать вопрос.`;
      }
      if (this.message.aborted) {
        const base = this.message.summary || '';
        return base ? `${base}\n\n— Соединение прервано` : '— Соединение прервано';
      }
      return this.message.summary || '';
    },
    hasMethodology() {
      return Boolean(this.message.methodology || this.message.sql_used);
    },
    hasContent() {
      return this.hasText || this.message.chart || (this.message.steps && this.message.steps.length);
    },
  },
};
</script>

<style scoped lang="scss">
.assistant-row {
  margin: 22px 0;
  font-size: 15px;
  line-height: 1.65;

  &.is-error .content {
    color: #842029;
  }
  &.is-aborted .content {
    color: #6e6e80;
  }
}

.thinking {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: var(--ai-text-muted);
  font-size: 14px;

  .dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--ai-text-muted);
    animation: blink 1.4s infinite ease-in-out both;
  }
  .dot:nth-child(1) { animation-delay: 0s; }
  .dot:nth-child(2) { animation-delay: .2s; }
  .dot:nth-child(3) { animation-delay: .4s; }
  .thinking-text { margin-left: 8px; }
}
@keyframes blink {
  0%, 80%, 100% { opacity: .25; transform: scale(.85); }
  40% { opacity: 1; transform: scale(1); }
}

.plain { white-space: pre-wrap; }

.summary { margin-bottom: 4px; }

.md-body ::v-deep p { margin: 0 0 12px; }
.md-body ::v-deep p:last-child { margin-bottom: 0; }
.md-body ::v-deep ul,
.md-body ::v-deep ol { margin: 8px 0 12px; padding-left: 22px; }
.md-body ::v-deep li { margin: 4px 0; }
.md-body ::v-deep strong { font-weight: 600; }
.md-body ::v-deep code {
  background: var(--ai-user-bubble);
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 13px;
  font-family: var(--ai-font-mono);
}
.md-body ::v-deep h1,
.md-body ::v-deep h2,
.md-body ::v-deep h3,
.md-body ::v-deep h4 {
  margin: 16px 0 8px;
  font-weight: 600;
  line-height: 1.3;
}
.md-body ::v-deep h1 { font-size: 22px; }
.md-body ::v-deep h2 { font-size: 19px; }
.md-body ::v-deep h3 { font-size: 16px; }
.md-body ::v-deep a { color: #1a73e8; text-decoration: underline; }
.md-body ::v-deep table {
  border-collapse: collapse;
  margin: 10px 0;
  font-size: 13.5px;
}
.md-body ::v-deep th,
.md-body ::v-deep td {
  border: 1px solid var(--ai-border);
  padding: 6px 10px;
  text-align: left;
}
.md-body ::v-deep th {
  background: var(--ai-user-bubble);
  font-weight: 600;
}
.md-body ::v-deep blockquote {
  border-left: 3px solid var(--ai-border);
  margin: 8px 0;
  padding: 2px 12px;
  color: var(--ai-text-muted);
}
.md-body ::v-deep pre {
  background: #f6f8fa;
  color: var(--ai-text);
  padding: 12px 14px;
  border-radius: 8px;
  overflow-x: auto;
  font-size: 13px;
  font-family: var(--ai-font-mono);
  margin: 10px 0;
}

.details { margin-top: 10px; }
.link-btn {
  background: transparent;
  border: none;
  padding: 0;
  color: var(--ai-text-muted);
  font-size: 13px;
  cursor: pointer;

  &:hover { color: var(--ai-text); }
}
.details-body {
  margin-top: 8px;
  padding: 12px 14px;
  background: var(--ai-user-bubble);
  border-radius: 10px;
}
.block + .block { margin-top: 10px; }
.label {
  font-size: 11.5px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: .04em;
  color: var(--ai-text-muted);
  margin-bottom: 4px;
}
.value {
  font-size: 13.5px;
  white-space: pre-wrap;
  line-height: 1.55;
}
.sql {
  margin: 0;
  padding: 10px 12px;
  background: #0d1117;
  border-radius: 8px;
  font-size: 12.5px;
  font-family: var(--ai-font-mono);
  white-space: pre-wrap;
  word-break: break-word;
  overflow-x: auto;

  code { background: transparent; }
}
</style>
